import { formatMoney, type Currency } from '@devizy/shared';
import * as React from 'react';

import { cn } from '../../lib/cn';
import { Badge } from '../badge';
import { Input } from '../input';

import { computeAdjustmentIndicator } from './adjustment-indicator';

import type { QuoteViewLabels } from './types';

export interface QuoteSummaryProps {
  subtotalHt: number;
  globalAdjustment: number | null;
  totalHt: number;
  currency: Currency;
  requesterCurrency?: Currency | null;
  indicativeTotal: number | null;
  conversionUnavailable: boolean;
  showIncompleteWarning: boolean;
  editable: boolean;
  onGlobalAdjustmentChange?: (globalAdjustment: number | null) => void;
  labels: QuoteViewLabels;
}

export function QuoteSummary({
  subtotalHt,
  globalAdjustment,
  totalHt,
  currency,
  requesterCurrency,
  indicativeTotal,
  conversionUnavailable,
  showIncompleteWarning,
  editable,
  onGlobalAdjustmentChange,
  labels,
}: QuoteSummaryProps): React.ReactElement {
  const indicator = computeAdjustmentIndicator(1, subtotalHt, globalAdjustment);
  const hasAdjustment = typeof globalAdjustment === 'number' && globalAdjustment !== 0;
  const exceedsSubtotal = totalHt < 0;

  return (
    <div className="border-input mt-4 flex flex-col gap-2 rounded-lg border p-3">
      {editable || hasAdjustment ? (
        <div className="text-muted-foreground flex items-center justify-between text-xs">
          <span>{labels.subtotalHT}</span>
          <span data-testid="quote-subtotal-ht">{formatMoney(subtotalHt, currency)}</span>
        </div>
      ) : null}

      {editable ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <label className="text-muted-foreground flex flex-col gap-1 text-xs">
            <span>
              {labels.globalAdjustment} ({currency})
            </span>
            <span className="text-muted-foreground/80">{labels.globalAdjustmentHint}</span>
          </label>
          <div className="flex flex-col items-end gap-1">
            <Input
              type="number"
              step={0.01}
              className="w-40"
              value={globalAdjustment ?? ''}
              onChange={(e) =>
                onGlobalAdjustmentChange?.(e.target.value === '' ? null : Number(e.target.value))
              }
              aria-label={labels.globalAdjustment}
              data-testid="quote-global-adjustment"
            />
            {indicator ? (
              <Badge
                variant="outline"
                className={cn(
                  'w-fit',
                  indicator.direction === 'reduction'
                    ? 'border-warning/30 bg-warning-tint text-warning-strong'
                    : 'border-destructive/25 bg-destructive-tint text-destructive-strong',
                )}
              >
                {indicator.direction === 'reduction' ? '−' : '+'}
                {indicator.percent} %
              </Badge>
            ) : null}
          </div>
        </div>
      ) : hasAdjustment ? (
        <div className="text-muted-foreground flex items-center justify-between text-xs">
          <span>{labels.globalAdjustment}</span>
          <span>{formatMoney(globalAdjustment, currency)}</span>
        </div>
      ) : null}

      <div className="flex items-center justify-between text-sm">
        <span className="font-medium">{labels.totalHT}</span>
        <span className="font-semibold" data-testid="quote-total-ht">
          {formatMoney(totalHt, currency)}
        </span>
      </div>
      {indicativeTotal !== null && requesterCurrency ? (
        <p className="text-muted-foreground text-xs" data-testid="quote-indicative-total">
          ≈ {formatMoney(indicativeTotal, requesterCurrency)} — {labels.indicative} {currency}
        </p>
      ) : null}
      {conversionUnavailable && requesterCurrency ? (
        <p className="text-warning text-xs" data-testid="quote-indicative-unavailable">
          {labels.indicativeUnavailable(requesterCurrency)}
        </p>
      ) : null}
      {exceedsSubtotal ? (
        <p className="text-destructive text-xs">{labels.globalAdjustmentTooLow}</p>
      ) : null}
      {showIncompleteWarning ? (
        <p className="text-warning text-xs">{labels.incompleteWarning}</p>
      ) : null}
    </div>
  );
}
